import React, { useEffect, useState, useCallback } from "react";
import { Typography, Box } from "@mui/material";
import { styled } from "@mui/system";
import ups from "../Images/company-logos/ups.png";
import pasternakShoham from "../Images/company-logos/pasternak_shoham.png";
import amisraGas from "../Images/company-logos/amisra_gas.png";
import flyingCargo from "../Images/company-logos/flying_cargo.png";
import dalas from "../Images/company-logos/dalas.png";
import ichilov from "../Images/company-logos/ichilov.png";
import lamda from "../Images/company-logos/lamda.png";
import spiro_films from "../Images/company-logos/spiro_films.png";

const rectImageHeight = "80px";
const squareImageHeight = "115px";
const company_logos = [
  { logo: amisraGas, alt: "אמישראגז", imageHeight: rectImageHeight },
  { logo: ups, alt: "ups", imageHeight: squareImageHeight },
  { logo: pasternakShoham, alt: "פסטרנק שוהם", imageHeight: squareImageHeight },
  { logo: flyingCargo, alt: "Flying Cargo", imageHeight: squareImageHeight },
  { logo: dalas, alt: "Dalas", imageHeight: rectImageHeight },
  { logo: ichilov, alt: "איכילוב", imageHeight: squareImageHeight },
  { logo: lamda, alt: "Lambda", imageHeight: squareImageHeight },
  { logo: spiro_films, alt: "Spiro Films", imageHeight: squareImageHeight },
];
const scrollSpeed = 40; // Speed of the scroll (higher is slower)

const Container = styled(Box)`
  display: flex;
  overflow: hidden;
  width: 85%;
  padding: 0;
  border-bottom: 2px solid black;
  border-top: 2px solid black;
`;

const ScrollWrapper = styled(Box)`
  display: flex;
  animation: scroll ${scrollSpeed}s linear infinite;
  width: max-content;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
  }
`;

const ImageContainer = styled(Box)`
  min-width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 20px 0;
`;

const useImageLoader = (images, timeout = 5000) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const loadedImagesRef = React.useRef(0);

  const resetLoader = useCallback(() => {
    loadedImagesRef.current = 0;
    setImagesLoaded(false);
    setShouldRefresh(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    loadedImagesRef.current += 1;
    if (loadedImagesRef.current === images.length) {
      setImagesLoaded(true);
    }
  }, [images.length]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!imagesLoaded) {
        setShouldRefresh(true);
      }
    }, timeout);

    return () => clearTimeout(timeoutId);
  }, [imagesLoaded, timeout]);

  useEffect(() => {
    if (shouldRefresh) {
      resetLoader();
      // Pre-load images
      images.forEach(img => {
        const image = new Image();
        image.src = img.logo;
        image.onload = handleImageLoad;
      });
    }
  }, [shouldRefresh, images, handleImageLoad, resetLoader]);

  return { imagesLoaded, handleImageLoad };
};

const Companies = ({ content, isMobile, accessibilityOptions }) => {
  const { imagesLoaded, handleImageLoad } = useImageLoader(company_logos);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight={isMobile ? "250px" : "380px"}
      sx={{
        backgroundColor: accessibilityOptions.contrast === "low" ? "#C2D3FC" : "rgba(74, 105, 123, 0.1)",
      }}
    >
      <Typography
        variant={isMobile ? "mobileH2" : "h2"}
        sx={{
          pb: isMobile ? 2 : 10,
        }}
      >
        {content.title}
      </Typography>
      <Container>
        <ScrollWrapper style={{ visibility: imagesLoaded ? 'visible' : 'hidden' }}>
          <ImageContainer>
            {company_logos.map((company, index) => (
              <img
                key={index}
                title={company.alt}
                src={company.logo}
                alt={company.alt}
                height={company.imageHeight}
                style={{ objectFit: 'contain' }}
                onLoad={handleImageLoad}
              />
            ))}
          </ImageContainer>
          <ImageContainer>
            {company_logos.map((company, index) => (
              <img
                key={`duplicate-${index}`}
                title={company.alt}
                src={company.logo}
                alt={company.alt}
                height={company.imageHeight}
                style={{ objectFit: 'contain' }}
                onLoad={handleImageLoad}
              />
            ))}
          </ImageContainer>
        </ScrollWrapper>
      </Container>
    </Box>
  );
};

export default Companies;
